/**
 * The books that are being built right now, above the shelf.
 *
 * Adding a book here is not a download, it is a chain that runs for hours:
 * fetch, translate, narrate with piper, align the recording to the words, map
 * the sentences onto the scanned pages. The reader pressed one button and then
 * closed the tab. This strip is the only thing that tells them the button did
 * anything at all.
 *
 * Three decisions worth keeping:
 *
 * - **It names the step, not a percentage.** A bar that sits at 62 % for ninety
 *   minutes says less than the words "narration and lining up the words", which
 *   say both what is happening and why it is slow.
 * - **It polls only while something is moving.** An idle library asks nothing;
 *   a queue with a job in it asks every fifteen seconds. The `readcommons:queued`
 *   event wakes it the instant a book is added from the search panel, so the row
 *   never appears fifteen seconds after the click.
 * - **A failure stays on screen** with the reason, in the reader's language,
 *   until it is retried or dismissed. A book that quietly stopped at step three
 *   is indistinguishable from one that was never asked for.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { api } from "../lib/api";
import { href } from "../lib/route";
import { t, useUiLang, type Key } from "../i18n";
import type { AcquireJob, AcquireQueue } from "../lib/types";

const POLL_MS = 15_000;

const STEP_LABEL: Record<string, Key> = {
  fetch: "stepFetch",
  install: "stepInstall",
  translate: "stepTranslate",
  voice: "stepVoice",
  index: "stepIndex",
  clean: "stepClean",
  narrate: "stepNarrate",
  cover: "stepCover",
};

const MOVING = new Set(["queued", "running"]);

/** The queue, refreshed only while it has something in it. */
export function useQueue(): {
  queue: AcquireQueue;
  refresh: () => void;
  drop: (id: number) => void;
} {
  const [queue, setQueue] = useState<AcquireQueue>({ jobs: [], worker: true });
  const timer = useRef<number | null>(null);

  const refresh = useCallback(() => {
    api
      .queue()
      .then(setQueue)
      // A queue that cannot be read is not an error worth a banner: the shelf
      // behind it works, and the next poll will say the same thing or better.
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    refresh();
    const wake = () => refresh();
    window.addEventListener("readcommons:queued", wake);
    return () => window.removeEventListener("readcommons:queued", wake);
  }, [refresh]);

  const active = queue.jobs.some((job) => MOVING.has(job.state));
  useEffect(() => {
    if (!active) return;
    timer.current = window.setInterval(refresh, POLL_MS);
    return () => {
      if (timer.current !== null) window.clearInterval(timer.current);
    };
  }, [active, refresh]);

  const drop = useCallback(
    (id: number) => {
      api
        .unqueue(id)
        .then(setQueue)
        .catch(() => refresh());
    },
    [refresh],
  );

  return { queue, refresh, drop };
}

function Bar({ job }: { job: AcquireJob }) {
  const done = job.steps > 0 ? Math.min(job.position / job.steps, 1) : 0;
  return (
    <span
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={job.steps}
      aria-valuenow={job.position}
      aria-label={t("prepStep", { n: job.position + 1, total: job.steps })}
      className="mt-2 block h-0.5 w-full overflow-hidden rounded-full bg-[var(--color-line)]"
    >
      <span
        className="block h-full rounded-full bg-[var(--color-accent)] transition-[width] duration-500"
        style={{ width: `${Math.round(done * 100)}%` }}
      />
    </span>
  );
}

export function PreparingRow({
  queue,
  onDrop,
}: {
  queue: AcquireQueue;
  onDrop: (id: number) => void;
}) {
  useUiLang();
  const shown = queue.jobs.filter((job) => job.state !== "cancelled");
  if (shown.length === 0) return null;

  return (
    <section aria-labelledby="preparing-heading" className="mb-10">
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-3">
        <h2
          id="preparing-heading"
          className="text-2xs font-medium uppercase tracking-[0.16em] text-[var(--color-muted)]"
        >
          {t("prepTitle")}
        </h2>
        {!queue.worker && (
          <span className="text-2xs text-[var(--color-faint)]">{t("addNoWorker")}</span>
        )}
      </div>

      <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {shown.map((job) => {
          const label = STEP_LABEL[job.step];
          return (
            <li
              key={job.id}
              className="flex items-center gap-3 rounded-xl bg-[var(--color-surface)] px-3 py-3 ring-1 ring-[var(--color-line)]"
            >
              {job.cover_url ? (
                <img
                  src={job.cover_url}
                  alt=""
                  loading="lazy"
                  className="h-14 w-10 shrink-0 rounded-sm object-cover ring-1 ring-[var(--color-line)]"
                />
              ) : (
                <span
                  aria-hidden="true"
                  className="h-14 w-10 shrink-0 rounded-sm bg-[var(--color-raised)] ring-1 ring-[var(--color-line)]"
                />
              )}

              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-[var(--color-ink)]">{job.title}</p>
                <p className="truncate text-xs text-[var(--color-faint)]">
                  {job.state === "failed"
                    ? t("prepFailed", { message: job.message })
                    : job.state === "done"
                      ? // The button next to this one already says "ready to
                        // read". Repeating it here spends the only line that
                        // could say which book it is: two identical words
                        // stacked read as a rendering fault, not as a status.
                        job.author || t("prepReady")
                      : label
                        ? t(label)
                        : t("prepStep", { n: job.position + 1, total: job.steps })}
                </p>
                {MOVING.has(job.state) && <Bar job={job} />}
              </div>

              {job.state === "done" && job.book_id ? (
                <a
                  href={href({ name: "book", id: job.book_id })}
                  className="tap shrink-0 rounded-lg px-3 py-1.5 text-xs font-medium coarse:min-h-11 bg-[var(--color-accent-soft)] text-[var(--color-accent)] transition-colors duration-150 hover:bg-[var(--color-accent)] hover:text-[var(--color-surface)]"
                >
                  {t("prepReady")}
                </a>
              ) : job.state === "queued" ? (
                <button
                  type="button"
                  onClick={() => onDrop(job.id)}
                  title={t("prepCancel")}
                  aria-label={t("prepCancel")}
                  className="tap shrink-0 rounded-lg p-2 text-[var(--color-faint)] transition-colors duration-150 hover:bg-[var(--color-raised)] hover:text-[var(--color-ink)]"
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    aria-hidden="true"
                  >
                    <path d="M6 6l12 12M18 6L6 18" />
                  </svg>
                </button>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
